import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import {
  MASTERDATA_SUPPLIER,
  MasterdataService,
  MasterdataSupplier,
} from '@softapps/core/masterdata';
import { ProductRepository } from '@softapps/core/product-info';
import { SupplierProfileRepository } from '../repositories/supplier-profile.repository';
import { SupplierMetadata } from '../types/extraction-result';
import {
  defaultProductId,
  DtiManualProduct,
  DtiSupplierProfile,
  toManualProduct,
  toSupplierProfile,
} from '../types/supplier-profile';
import { pickSupplier } from '../utils/supplier-resolution';

const SUPPLIER_SEARCH_LIMIT = 10;

export type SupplierProfileState =
  | 'idle'
  | 'resolving'
  /** No Softline supplier could be derived from the document — pick one. */
  | 'unresolved'
  /** Supplier known, but it has no DTI profile. */
  | 'no-profile'
  | 'ready';

/**
 * Softline supplier of the reviewed document and its DTI profile. The supplier
 * is derived from the document metadata (UID, then name) and can be overridden
 * with the header picker; the profile follows the supplier.
 */
@Injectable()
export class SupplierProfileService {
  private readonly masterdata = inject(MasterdataService);
  private readonly repository = inject(SupplierProfileRepository);
  private readonly products = inject(ProductRepository);
  private readonly injector = inject(Injector);

  private readonly _supplier = signal<MasterdataSupplier | null>(null);
  private readonly _profile = signal<DtiSupplierProfile | null>(null);
  private readonly _loading = signal(false);
  private readonly _resolved = signal(false);
  /** Invalidates in-flight lookups after a reset or a newer choice. */
  private runId = 0;

  readonly supplier = this._supplier.asReadonly();
  readonly profile = this._profile.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly manualProduct = computed(() => this._profile()?.manualProduct ?? null);
  readonly manualProductAvailable = computed(() => !!this.manualProduct());

  readonly state = computed<SupplierProfileState>(() => {
    if (this._loading()) return 'resolving';
    if (!this._resolved()) return 'idle';
    if (!this._supplier()) return 'unresolved';
    return this._profile() ? 'ready' : 'no-profile';
  });

  reset(): void {
    this.runId++;
    this._supplier.set(null);
    this._profile.set(null);
    this._loading.set(false);
    this._resolved.set(false);
  }

  /** Derives the supplier from the document; a lookup failure ends unresolved. */
  async resolveFromDocument(document: SupplierMetadata): Promise<void> {
    const run = ++this.runId;
    this._loading.set(true);
    try {
      const candidates = await this.searchCandidates(document);
      if (run !== this.runId) return;
      await this.apply(pickSupplier(candidates, document), run);
    } catch (e) {
      handleRequestError(e, this.injector, { show: false });
      if (run === this.runId) await this.apply(null, run);
    }
  }

  /** Header picker — the user's choice replaces whatever was derived. */
  async setSupplier(supplier: MasterdataSupplier | null): Promise<void> {
    const run = ++this.runId;
    this._loading.set(true);
    await this.apply(supplier, run);
  }

  private async searchCandidates(
    document: SupplierMetadata
  ): Promise<MasterdataSupplier[]> {
    const terms = [document.uid?.trim(), document.name?.trim()].filter(
      (term): term is string => !!term
    );
    const results = await Promise.all(
      terms.map((term) =>
        this.masterdata.search<MasterdataSupplier>(MASTERDATA_SUPPLIER, term, {
          limit: SUPPLIER_SEARCH_LIMIT,
        })
      )
    );
    const seen = new Set<number>();
    return ([] as MasterdataSupplier[])
      .concat(...results)
      .filter((s) => !seen.has(s.id) && !!seen.add(s.id));
  }

  private async apply(supplier: MasterdataSupplier | null, run: number): Promise<void> {
    const profile = supplier ? await this.loadProfile(String(supplier.id)) : null;
    if (run !== this.runId) return;
    this._supplier.set(supplier);
    this._profile.set(profile);
    this._resolved.set(true);
    this._loading.set(false);
  }

  /** Both halves in parallel; a failing half is reported and treated as not configured. */
  private async loadProfile(supplierId: string): Promise<DtiSupplierProfile | null> {
    const [defaultProduct, unitMappings] = await Promise.allSettled([
      this.repository.loadDefaultProduct(supplierId),
      this.repository.loadUnitMappings(supplierId),
    ]);
    for (const result of [defaultProduct, unitMappings]) {
      if (result.status === 'rejected') {
        handleRequestError(result.reason, this.injector, { show: true });
      }
    }
    const productId =
      defaultProduct.status === 'fulfilled' ? defaultProductId(defaultProduct.value) : null;
    return toSupplierProfile(
      supplierId,
      productId ? await this.loadManualProduct(productId) : null,
      unitMappings.status === 'fulfilled' ? unitMappings.value : []
    );
  }

  /** The default-product response is the id only; key/description need the product record. */
  private async loadManualProduct(productId: string): Promise<DtiManualProduct> {
    try {
      return toManualProduct(productId, await this.products.getById(productId));
    } catch (e) {
      // The id alone still books the rows correctly — only the label is missing.
      handleRequestError(e, this.injector, { show: false });
      return toManualProduct(productId, null);
    }
  }
}
